import { Box, Chip, Typography } from '@mui/material'
import React from 'react'
import { orderPanelStyles } from './common/orderPanelStyles'
import OrderUserAvatar from './OrderUserAvatar'
import OrderInfoCard from './OrderInfoCard'
import { mainBlue, mainGreen, mainRed } from '../utils/color'
import { useSelector } from 'react-redux'
import { OrderStatus } from '../models'

export default function PanelOrderItem({ order, index, expandedOrders, renderActions }) {
	const { currentUser } = useSelector((s) => s.currentUser)
	const orderTitle = getTitleOrder(order, currentUser)
	const avatarUrl = getAvatar(order)
	const titleColor = getTitleColor(order)
	const timeStamp = getTime(order)

	return (
		<Box key={order?._id || index} sx={orderPanelStyles.orderCard}>
			{/* Header with Avatar and Title */}
			<Box sx={orderPanelStyles.header}>
				<OrderUserAvatar url={avatarUrl} />
				<Typography
					sx={{
						...orderPanelStyles.title,
						color: titleColor,
					}}>
					{`${orderTitle} ${timeStamp}`}
				</Typography>
			</Box>

			{/* Replace existing Tags section with OrderStatuses component */}
			<OrderStatuses order={order} />

			{/* Use OrderInfoCard to display order details */}
			<OrderInfoCard orderData={order} isExpanded={expandedOrders[order?._id]} />
			{renderActions && renderActions(order)}
		</Box>
	)
}

// Cập nhật component OrderStatuses
const OrderStatuses = ({ order }) => {
	const chipStyles = {
		shiftChip: {
			backgroundColor: mainGreen,
			color: 'white',
			width: '50px',
			borderRadius: '16px',
			fontWeight: 500,
			textAlign: 'center',
			height: '24px',
		},
		redChip: {
			backgroundColor: mainRed,
			color: 'white',
			fontWeight: 500,
			borderRadius: '16px',
			fontSize: '12px',
			height: '24px',
		},
		greyChip: {
			backgroundColor: '#e0e0e0',
			color: 'grey',
			fontWeight: 500,
			borderRadius: '16px',
			fontSize: '12px',
			height: '24px',
		},
	}

	return (
		<Box
			sx={{
				display: 'flex',
				gap: '4px',
				flexWrap: 'wrap',
				alignItems: 'center',
			}}>
			{/* Shift Code Chip với tên hiển thị */}
			<Chip label={`${order?.shiftCode || 'S'}${order?.shiftIndex || ''}`} sx={chipStyles.shiftChip} />

			{/* App Tag */}
			{order?.app && <Chip label='App' sx={chipStyles.redChip} />}

			{/* Quick Purchase Tag */}
			{order?.quickPurchase && <Chip label='Mua nhanh' sx={chipStyles.redChip} />}

			{/* Payment Transfer Tag */}
			{order?.payment === 'transfer' && <Chip label='CK' sx={chipStyles.redChip} />}

			{/* Pending Confirmation Tag */}
			{(order?.status === OrderStatus.PENDING_PRODUCE ||
				(order?.quickPurchase && order?.status === OrderStatus.PENDING_PRODUCE_QUICK)) && (
				<Chip label='Chờ xác nhận' sx={chipStyles.greyChip} />
			)}

			{/* Pending Delete Tag */}
			{OrderStatus.isPendingDelete(order?.status) && <Chip label='Chờ hủy' sx={chipStyles.redChip} />}

			{/* Confirm Delete Tag */}
			{OrderStatus.isConfirmDelete(order?.status) && <Chip label='Đã hủy' sx={chipStyles.redChip} />}

			{/* Edited Tag */}
			{order?.edited && <Chip label='Đã sửa' sx={chipStyles.greyChip} />}
		</Box>
	)
}

function getTitleColor(order) {
	switch (order?.status) {
		case OrderStatus.PRODUCE:
			// Kiểm tra điều kiện produceEnd
			if (order?.producer?.produceEnd == null) {
				return mainRed
			}
			return mainBlue

		case OrderStatus.SHIP:
			return mainRed

		default:
			return null
	}
}

function getTime(order) {
	switch (order?.status) {
		case OrderStatus.CREATE:
		case OrderStatus.PENDING_PRODUCE:
			return order?.transferStore != null
				? (order?.transferStore?.timeStamp ?? order?.creator?.createdAt ?? '')
				: (order?.creator?.createdAt ?? '')

		case OrderStatus.PRODUCE:
		case OrderStatus.PENDING_DELETE_PRODUCE:
		case OrderStatus.CONFIRM_DELETE_PRODUCE:
			return order?.producer?.produceEnd == null
				? (order?.producer?.produceStart ?? '')
				: (order?.producer?.produceEnd ?? '')

		case OrderStatus.SHIP:
		case OrderStatus.PENDING_DELETE_SHIP:
		case OrderStatus.CONFIRM_DELETE_SHIP:
			return order?.shipper?.shipStart ?? ''

		case OrderStatus.SUCCESS:
		case OrderStatus.PENDING_PRODUCE_QUICK:
		case OrderStatus.PENDING_DELETE_SUCCESS:
		case OrderStatus.CONFIRM_DELETE_SUCCESS:
			return order?.shipper?.shipEnd ?? ''

		default:
			return ''
	}
}

function getTitleOrder(order, user) {
	switch (order?.status) {
		case OrderStatus.CREATE:
		case OrderStatus.PENDING_PRODUCE:
			const creatorName = order?.creator?.user?.name ?? '(Không có tên)'
			const transferStoreUser = order?.transferStore?.user?.name ?? '(Không có tên)'
			const transferFromStore = order?.transferStore?.fromStore?.name ?? ''

			return order?.transferStore != null
				? `${transferStoreUser} chuyển đơn từ ${transferFromStore} vào`
				: `${creatorName} tạo đơn vào`

		case OrderStatus.PRODUCE:
		case OrderStatus.PENDING_DELETE_PRODUCE:
		case OrderStatus.CONFIRM_DELETE_PRODUCE:
			const producerName = order?.producer?.user?.name ?? '(Không có tên)'
			const actionText =
				order?.producer?.produceEnd == null ? 'đã xuất kho và chờ soạn đơn vào' : 'hoàn thành xử lý đơn vào'
			return `${producerName} ${actionText}`

		case OrderStatus.SHIP:
		case OrderStatus.PENDING_DELETE_SHIP:
		case OrderStatus.CONFIRM_DELETE_SHIP:
			const shipperNameShip = order?.shipper?.user?.name ?? '(Không có tên)'
			if (user?.id === order?.shipper?.user?._id) {
				return `${shipperNameShip} đã nhận giao hàng`
			}
			return `${shipperNameShip} đang giao hàng`

		case OrderStatus.SUCCESS:
		case OrderStatus.PENDING_PRODUCE_QUICK:
		case OrderStatus.CONFIRM_DELETE_SUCCESS:
		case OrderStatus.PENDING_DELETE_SUCCESS:
			const confirmShipUserName = order?.confirmShip?.user?.name
			const shipperNameSuccess = order?.shipper?.user?.name
			const successUserName = confirmShipUserName ?? shipperNameSuccess ?? '(Không có tên)'
			return `${successUserName} giao hàng thành công`

		default:
			return order?.creator?.user?.name ? `${order.creator.user.name} đã tạo đơn` : 'Trạng thái không xác định' // Có thể thay đổi default text
	}
}

function getAvatar(order) {
	switch (order?.status) {
		case OrderStatus.CREATE:
		case OrderStatus.PENDING_PRODUCE:
			return order?.transferStore != null ? order?.transferStore?.user?.photo : order?.creator?.user?.photo

		case OrderStatus.PRODUCE:
		case OrderStatus.PENDING_DELETE_PRODUCE:
		case OrderStatus.CONFIRM_DELETE_PRODUCE:
			return order?.producer?.user?.photo

		case OrderStatus.SHIP:
		case OrderStatus.CONFIRM_DELETE_SHIP:
		case OrderStatus.PENDING_DELETE_SHIP:
			return order?.shipper?.user?.photo

		case OrderStatus.SUCCESS:
		case OrderStatus.PENDING_PRODUCE_QUICK:
		case OrderStatus.PENDING_DELETE_SUCCESS:
		case OrderStatus.CONFIRM_DELETE_SUCCESS:
			return order?.confirmShip?.user?.photo ?? order?.shipper?.user?.photo

		default:
			return ''
	}
}

